/**
 * Реестр шрифтов редакторов — единственное место, где объявлен шрифт.
 *
 * Отсюда берут список выбора оба редактора (медиа-менеджер и трансляции), начертания —
 * «Начертание», загрузку — `loadAppFonts` и ожидание на канве — `ensureFonts`. Новый шрифт —
 * одна запись здесь и больше ни одного файла (плюс парная правка сервиса рендера
 * `imgame-media-manager-render`: у него свой набор файлов шрифтов).
 *
 * Два обязательных условия для записи с `inPicker: true`:
 *
 * 1. **Кириллица во всех весах.** Продукт русскоязычный: шрифт, который рисует латиницу, а
 *    кириллицу отдаёт запасному, хуже отсутствующего. Проверять по CSS2 API Google — блок
 *    `/* cyrillic *\/` у **каждого** веса, а не у семейства (`.claude/docs/editor-fonts.md`).
 * 2. **Шрифт реально загружается** (`source` не `system`): системный шрифт на сервере рендера
 *    подменяется (Liberation / DejaVu), а в OBS зависит от машины оператора.
 *
 * `inPicker: false` — шрифт грузится, но в списке не показывается: на нём есть сохранённые табло
 * (Big Shoulders Display, Saira Stencil One — без кириллицы) или это системный шрифт старых
 * шаблонов. Удалять такую запись нельзя — старое табло перестанет грузить свой шрифт.
 */

/** Веса, которые умеет «Начертание». 800 нет: его нет ни у одного шрифта набора, кроме SF Pro. */
export type FontWeightValue = 300 | 400 | 500 | 600 | 700 | 900;

/**
 * Роль шрифта в наборе. Набор собирается по ролям, а не по «хорошим шрифтам»: почти одинаковые
 * гротески рядом выглядят как ошибка, контраст дают ширина и класс (`.claude/docs/editor-fonts.md`,
 * «Состав набора»). Порядок ролей — порядок списка выбора.
 */
export type FontCategory = 'text' | 'condensed' | 'display' | 'tech' | 'slab' | 'serif' | 'script' | 'mono';

/**
 * Акцидентные роли: шрифт с характером, которым набирают заголовок или акцент, а не текст.
 * Одно начертание для них — норма (Russo One, Lobster), правило «не меньше трёх» на них не действует.
 */
export const ACCENT_CATEGORIES: readonly FontCategory[] = ['display', 'script'];

/** `google` — CSS2 API Google Fonts; `local` — `@font-face` в `src/index.css`; `system` — шрифт ОС. */
export type FontSource = 'google' | 'local' | 'system';

export type FontFallback = 'sans-serif' | 'serif' | 'monospace' | 'cursive';

export interface FontEntry {
  /** Имя семейства так, как его пишут в `fontFamily` и в CSS2 API. */
  family: string;
  category: FontCategory;
  source: FontSource;
  /** Реальные веса семейства — по возрастанию. «Начертание» предлагает только их. */
  weights: FontWeightValue[];
  fallback: FontFallback;
  cyrillic: boolean;
  inPicker: boolean;
  /** Метрически совместимая замена системного шрифта: те же ширины, текст не перетекает. */
  replaces?: string;
}

const LIGHT_TO_BOLD: FontWeightValue[] = [300, 400, 500, 600, 700];
const ALL_WEIGHTS: FontWeightValue[] = [300, 400, 500, 600, 700, 900];
const REGULAR_TO_BLACK: FontWeightValue[] = [400, 500, 600, 700, 900];
const REGULAR_BOLD: FontWeightValue[] = [400, 700];

/** Шрифт новых объектов, когда ничего не выбрано. */
export const DEFAULT_FONT_FAMILY = 'Open Sans';

type EntryOptions = Partial<Pick<FontEntry, 'fallback' | 'cyrillic' | 'inPicker' | 'replaces'>>;

const font = (
  family: string,
  source: FontSource,
  category: FontCategory,
  weights: FontWeightValue[],
  options: EntryOptions = {},
): FontEntry => ({ family, source, category, weights, fallback: 'sans-serif', cyrillic: true, inPicker: true, ...options });

/**
 * Порядок записей с `inPicker: true` — порядок списка выбора, сгруппированный по ролям. Веса и
 * кириллица у каждого веса проверены замером CSS2 API (`.claude/docs/editor-fonts.md`, «How
 * the set was checked»): лишний вес в записи — HTTP 400 на всю ссылку `loadAppFonts`, и не грузится ни один шрифт.
 */
export const FONT_REGISTRY: readonly FontEntry[] = [
  // Текст: подписи, мелкий текст.
  font('Open Sans', 'google', 'text', LIGHT_TO_BOLD),
  font('Inter', 'google', 'text', ALL_WEIGHTS),
  font('Golos Text', 'google', 'text', REGULAR_TO_BLACK),
  // Узкие — основа табло: фамилия влезает в плашку без уменьшения кегля.
  font('Oswald', 'google', 'condensed', LIGHT_TO_BOLD),
  // Цифры одной ширины по умолчанию — для счёта и таймера (канва не включает `tnum`).
  font('Roboto Condensed', 'google', 'condensed', ALL_WEIGHTS),
  font('Sofia Sans Extra Condensed', 'google', 'condensed', ALL_WEIGHTS),
  // Широкие и тяжёлые — заголовки и акценты.
  font('SF Pro Display', 'local', 'display', ALL_WEIGHTS),
  font('Montserrat', 'google', 'display', ALL_WEIGHTS),
  font('Unbounded', 'google', 'display', ALL_WEIGHTS),
  font('Dela Gothic One', 'google', 'display', [400]),
  font('Russo One', 'google', 'display', [400]),
  // Технические — киберспорт и «цифровой» стиль.
  font('Exo 2', 'google', 'tech', ALL_WEIGHTS),
  font('Tektur', 'google', 'tech', REGULAR_TO_BLACK),
  font('Roboto Slab', 'google', 'slab', ALL_WEIGHTS, { fallback: 'serif' }),
  // Антиквы.
  font('Playfair Display', 'google', 'serif', REGULAR_TO_BLACK, { fallback: 'serif' }),
  font('PT Serif', 'google', 'serif', REGULAR_BOLD, { fallback: 'serif', replaces: 'Georgia' }),
  // Рукописные — соцсети и поздравления.
  font('Caveat', 'google', 'script', [400, 500, 600, 700], { fallback: 'cursive' }),
  font('Lobster', 'google', 'script', [400], { fallback: 'cursive' }),
  // Моноширинные — таймеры и таблицы.
  font('JetBrains Mono', 'google', 'mono', LIGHT_TO_BOLD, { fallback: 'monospace' }),
  font('Cousine', 'google', 'mono', REGULAR_BOLD, { fallback: 'monospace', replaces: 'Courier New' }),

  // Убраны из выбора как двойники (SF Pro Text и Roboto — Inter, Arimo — Open Sans / Inter, Tinos — PT Serif),
  // но грузятся: на них уже могут быть сохранены шаблоны. Arimo и Tinos — метрические замены Arial и Times.
  font('SF Pro Text', 'local', 'text', ALL_WEIGHTS, { inPicker: false }),
  font('Roboto', 'google', 'text', ALL_WEIGHTS, { inPicker: false }),
  font('Arimo', 'google', 'text', [400, 500, 600, 700], { inPicker: false, replaces: 'Arial' }),
  font('Tinos', 'google', 'serif', REGULAR_BOLD, { fallback: 'serif', inPicker: false, replaces: 'Times New Roman' }),

  // Системные — только для старых шаблонов: на сервере рендера и в OBS их подменяют.
  font('Arial', 'system', 'text', REGULAR_BOLD, { inPicker: false }),
  font('Verdana', 'system', 'text', REGULAR_BOLD, { inPicker: false }),
  font('Georgia', 'system', 'serif', REGULAR_BOLD, { fallback: 'serif', inPicker: false }),
  font('Times New Roman', 'system', 'serif', REGULAR_BOLD, { fallback: 'serif', inPicker: false }),
  font('Courier New', 'system', 'mono', REGULAR_BOLD, { fallback: 'monospace', inPicker: false }),

  // Только загрузка: на них сохранены табло трансляций. Big Shoulders и Saira — без кириллицы;
  // Source Sans Pro — старое имя Source Sans 3, CSS2 API его ещё отдаёт.
  font('Big Shoulders Display', 'google', 'condensed', [400], { cyrillic: false, inPicker: false }),
  font('Saira Stencil One', 'google', 'display', [400], { cyrillic: false, inPicker: false }),
  font('Ubuntu', 'google', 'text', [300, 400, 500, 700], { inPicker: false }),
  font('Nunito Sans', 'google', 'text', ALL_WEIGHTS, { inPicker: false }),
  font('Source Sans Pro', 'google', 'text', [300, 400, 600, 700, 900], { inPicker: false }),
];

const normalizeFamilyKey = (family: string) => family.trim().toLowerCase();

/** Запись реестра по имени семейства (без учёта регистра); `undefined` — шрифта в наборе нет. */
export function findFontEntry(family: string | undefined): FontEntry | undefined {
  if (!family) return undefined;
  const key = normalizeFamilyKey(family);
  return FONT_REGISTRY.find((entry) => normalizeFamilyKey(entry.family) === key);
}
